import classNames from 'classnames';
import type { BadgeProps } from 'shared/ui/Badge';
import { Badge } from 'shared/ui/Badge';
import { ClippedText } from 'shared/ui/ClippedText';
import type { Column } from 'shared/ui/Table';
import type { HistoryModels, ChangeViews } from 'entities/history';

import { HistoryEventDate } from './HistoryEventDate';
import { getChangeDetail } from './GetChangeDetail';
import { EventDetailsCellWithCsvColumn } from './EventDetailsCellWithCsvColumn';
import { EventDetailsCellWithJsonColumn } from './EventDetailsCellWithJsonColumn';
import { Source } from './Source';

type Params<T> = {
  changeDetailMapper: (data: T) => ChangeViews;
  badgePropsConfig: Record<string, BadgeProps>;
  withSourceTooltip?: boolean;
  withUserColumn?: boolean;
  withCsvColumn?: {
    entityId: number;
    csvDownloader: (entityId: number) => {
      isLoading: boolean;
      download: (paramsForUrl?: Record<string, string> | undefined) => Promise<void>;
    };
    downloadableEvents: string[];
  };
  withJsonColumn?: {
    entityId: number;
    jsonDownloader: (
      entityId: number,
      historyId: number
    ) => {
      isLoading: boolean;
      download: (paramsForUrl?: Record<string, string> | undefined) => Promise<void>;
    };
  };
  columnClassNames?: {
    dateColumnClassName?: string;
    userColumnClassName?: string;
    eventColumnClassName?: string;
    eventDetailsColumnClassName?: string;
    csvColumnClassName?: string;
    jsonColumnClassName?: string;
  };
};

export const getHistoryTableColumns = <T extends HistoryModels>(params: Params<T>): Column<T>[] => {
  const columns: Column<T>[] = [];

  columns.push({
    label: 'DATE',
    key: 'DATE',
    columnCell: ({ row }) => {
      return (
        <td className={classNames('w-[95px] !pr-0', params.columnClassNames?.dateColumnClassName)}>
          <HistoryEventDate date={row.createdAt} />
        </td>
      );
    },
    headerCell: ({ value }) => <th>{value}</th>,
  });

  if (params.withUserColumn) {
    columns.push({
      label: 'USER',
      key: 'USER',
      columnCell: ({ row }) => {
        if (!('actor' in row)) {
          return <td />;
        }

        return (
          <td className={classNames('line-height-[18px]', params.columnClassNames?.userColumnClassName)}>
            <ClippedText
              text={row.actor}
              tooltip={{
                content: row.actor,
              }}
            />
          </td>
        );
      },
      headerCell: ({ value }) => <th>{value}</th>,
    });
  }

  columns.push({
    label: 'EVENT',
    key: 'EVENT',
    columnCell: ({ row }) => {
      return (
        <td className={classNames('px-[5px]', params.columnClassNames?.eventColumnClassName)}>
          {params.withSourceTooltip && 'source' in row ? (
            <div className="flex flex-col">
              <Badge {...params.badgePropsConfig[row.eventType]} />
              <Source
                source={row.source}
                className="mt-[6px]"
              />
            </div>
          ) : (
            <Badge {...params.badgePropsConfig[row.eventType]} />
          )}
        </td>
      );
    },
    headerCell: ({ value }) => <th>{value}</th>,
  });
  columns.push({
    label: 'EVENT DETAILS',
    key: 'EVENT DETAILS',
    columnCell: ({ row }) => {
      return (
        <td className={params.columnClassNames?.eventDetailsColumnClassName}>
          {getChangeDetail(params.changeDetailMapper(row))}
        </td>
      );
    },
    headerCell: ({ value }) => <th>{value}</th>,
  });

  if (params.withCsvColumn) {
    const { entityId, csvDownloader, downloadableEvents } = params.withCsvColumn;

    columns.push({
      label: 'CSV',
      key: 'CSV',
      columnCell: ({ row }) => {
        if (!downloadableEvents.includes(row.eventType)) {
          return <td />;
        }

        return (
          <EventDetailsCellWithCsvColumn
            csvDownloader={csvDownloader}
            entityId={entityId}
            className={classNames('text-center', params.columnClassNames?.csvColumnClassName)}
          />
        );
      },
      headerCell: ({ value }) => <th>{value}</th>,
    });
  }

  if (params.withJsonColumn) {
    const { entityId, jsonDownloader } = params.withJsonColumn;

    columns.push({
      label: 'JSON',
      key: 'JSON',
      columnCell: ({ row }) => (
        <EventDetailsCellWithJsonColumn
          row={row}
          entityId={entityId}
          jsonDownloader={jsonDownloader}
          className={classNames('text-center', params.columnClassNames?.jsonColumnClassName)}
        />
      ),
      headerCell: ({ value }) => <th>{value}</th>,
    });
  }

  return columns;
};
